<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref } from 'vue'
import Lenis from '@studio-freight/lenis'
import Header from '../components/HeaderView.vue'
import Footer from '../components/FooterView.vue'
import { blogApi } from '../lib/supabase'
import { useSEO } from '../composables/useSEO'
import type { Post } from '../lib/supabase'

let lenis: Lenis | null = null

// --- INICIO DE CAMBIOS ---

// 1. Crear estado reactivo para los posts
// CAMBIO: La estructura de `post_tags` ahora es más simple
const allPosts = ref<(Post & { author_name: string | null; post_tags: { name: string }[] })[]>([])
const featuredPost = ref<(typeof allPosts.value)[0] | null>(null)
const regularPosts = ref<typeof allPosts.value>([])
const isLoading = ref(true)
const errorMsg = ref<string | null>(null)
const { updateMetaTags } = useSEO()

// 2. Función para cargar los datos
async function fetchPosts() {
  try {
    isLoading.value = true
    // Usamos la función de la API que ya creaste
    const data = await blogApi.getPublishedPosts()

    console.log('📊 Posts obtenidos:', data) // Debug log

    // El tipo inferido de `data` es complejo, lo casteamos para mejor autocompletado
    const typedData = data as unknown as typeof allPosts.value

    allPosts.value = typedData

    console.log('📊 Total de posts:', typedData.length) // Debug log
    console.log('📊 Posts destacado:', typedData[0]?.title) // Debug log
    console.log(
      '📊 Posts regulares:',
      typedData.slice(1).map((p) => p.title),
    ) // Debug log

    // Lógica para separar destacados de regulares
    // Tomamos el primer post como destacado (el más reciente)
    featuredPost.value = typedData.length > 0 ? typedData[0] : null

    // Los posts regulares son todos excepto el destacado
    regularPosts.value = typedData.length > 1 ? typedData.slice(1) : []
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Error desconocido'
    errorMsg.value = `Error al cargar los posts: ${errorMessage}`
    console.error(error)
  } finally {
    isLoading.value = false
  }
}

// 3. Cargar los datos cuando el componente se monta
onMounted(async () => {
  await fetchPosts()

  // Actualizar SEO para la página del blog
  updateMetaTags({
    title: 'Blog - Fourin',
    description:
      'Descubre artículos sobre gestión de activos, mantenimiento predictivo y eficiencia operativa. Mantente actualizado con las últimas tendencias del sector.',
    url: 'https://fourin.co/blog',
    type: 'website',
  })

  // Inicializar Lenis
  lenis = new Lenis({
    lerp: 0.1,
    smoothWheel: true,
  })

  function raf(time: number) {
    lenis?.raf(time)
    requestAnimationFrame(raf)
  }

  requestAnimationFrame(raf)

  // Restablecer scroll al inicio de la página
  window.scrollTo({ top: 0, behavior: 'smooth' })
})

// --- FIN DE CAMBIOS ---

onBeforeUnmount(() => {
  if (lenis) {
    lenis.destroy()
  }
})
</script>

<template>
  <main>
    <Header />

    <!-- Hero Section -->
    <section class="blog-hero">
      <div class="container">
        <div class="breadcrumbs">
          <span>Inicio</span>
          <span class="separator">→</span>
          <span>Blog</span>
        </div>

        <div class="hero-content">
          <h1>Blog de Fourin</h1>
          <p class="hero-description">
            Explora las últimas tendencias en tecnología industrial, innovación y transformación
            digital. Nuestro equipo comparte insights, casos de estudio y perspectivas sobre el
            futuro de la industria.
          </p>
        </div>
      </div>
    </section>

    <!-- Skeleton Loader -->
    <div v-if="isLoading" class="skeleton-container">
      <div class="container">
        <!-- Featured Post Skeleton -->
        <section class="featured-post-skeleton">
          <div class="featured-skeleton-content">
            <div class="featured-skeleton-image"></div>
            <div class="featured-skeleton-details">
              <div class="skeleton-author">
                <div class="skeleton-avatar"></div>
                <div class="skeleton-author-text"></div>
              </div>
              <div class="skeleton-date"></div>
              <div class="skeleton-title"></div>
              <div class="skeleton-tags">
                <div class="skeleton-tag"></div>
                <div class="skeleton-tag"></div>
                <div class="skeleton-tag"></div>
              </div>
            </div>
          </div>
        </section>

        <!-- Posts Grid Skeleton -->
        <section class="posts-skeleton">
          <div class="section-header-skeleton">
            <div class="skeleton-section-title"></div>
            <div class="skeleton-button"></div>
          </div>

          <div class="posts-grid-skeleton">
            <div v-for="n in 6" :key="n" class="post-card-skeleton">
              <div class="skeleton-post-image"></div>
              <div class="skeleton-post-content">
                <div class="skeleton-post-category"></div>
                <div class="skeleton-post-title"></div>
                <div class="skeleton-post-excerpt"></div>
                <div class="skeleton-post-meta">
                  <div class="skeleton-post-author"></div>
                  <div class="skeleton-post-date"></div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="errorMsg" class="container">
      <div style="text-align: center; padding: 2rem; color: red">
        <p>{{ errorMsg }}</p>
      </div>
    </div>

    <!-- Featured Post -->
    <section class="featured-post" v-if="featuredPost && !isLoading">
      <div class="container">
        <div class="featured-content">
          <router-link :to="`/blog/${featuredPost.slug}`" class="featured-image-link">
            <div class="featured-image">
              <img
                :src="featuredPost.cover_image_url || '/placeholder-image.jpg'"
                :alt="featuredPost.title"
              />
              <div class="featured-overlay">
                <h2>{{ featuredPost.title }}</h2>
              </div>
            </div>
          </router-link>

          <div class="featured-details">
            <div class="post-meta">
              <div class="author-info">
                <span class="written-by">Escrito por</span>
                <div class="author-avatar">
                  <span>{{ (featuredPost.author_name || 'Autor').charAt(0) }}</span>
                </div>
                <span class="author-name">{{
                  featuredPost.author_name || 'Autor Desconocido'
                }}</span>
              </div>
              <span class="publish-date"
                >Publicado el
                {{
                  new Date(featuredPost.published_at || featuredPost.created_at).toLocaleDateString(
                    'es-ES',
                    { year: 'numeric', month: 'long', day: 'numeric' },
                  )
                }}</span
              >
            </div>

            <div class="post-categories">
              <span class="file-under">Etiquetas:</span>
              <div class="category-tags">
                <span
                  v-for="postTag in featuredPost.post_tags"
                  :key="postTag.name"
                  class="category-tag"
                >
                  {{ postTag.name }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Blog Posts Grid -->
    <section class="blog-posts" v-if="regularPosts.length > 0 && !isLoading">
      <div class="container">
        <div class="section-header">
          <h2>Artículos Destacados</h2>
          <button class="view-all-btn">Ver todos los artículos</button>
        </div>

        <div class="posts-grid">
          <router-link
            v-for="post in regularPosts"
            :key="post.id"
            :to="`/blog/${post.slug}`"
            class="post-card-link"
          >
            <article class="post-card">
              <div class="post-image">
                <img :src="post.cover_image_url || '/placeholder-image.jpg'" :alt="post.title" />
              </div>

              <div class="post-content">
                <div class="post-meta">
                  <span class="post-category">{{
                    post.post_tags[0]?.name || 'Sin categoría'
                  }}</span>
                  <span class="post-date">{{
                    new Date(post.published_at || post.created_at).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  }}</span>
                </div>

                <h3 class="post-title">{{ post.title }}</h3>
                <p class="post-excerpt">{{ post.title.substring(0, 100) }}...</p>

                <div class="post-footer">
                  <span class="post-author">{{ post.author_name || 'Autor Desconocido' }}</span>
                  <span class="post-read-time">5 min</span>
                </div>
              </div>
            </article>
          </router-link>
        </div>
      </div>
    </section>

    <Footer />
  </main>
</template>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Hero Section */
.blog-hero {
  padding: 100px 0 100px;
  background-image: url('../assets/images/bannerblog2.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
}

.blog-hero::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

.blog-hero .container {
  position: relative;
  z-index: 2;
}

.breadcrumbs {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #e2e8f0;
  margin-bottom: 40px;
}

.separator {
  color: #cbd5e1;
}

.hero-content h1 {
  font-size: 3.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 20px;
  line-height: 1.1;
}

.hero-description {
  font-size: 1.25rem;
  color: #e2e8f0;
  max-width: 600px;
  line-height: 1.6;
}

/* Featured Post */
.featured-post {
  padding: 80px 0;
  background: white;
}

.featured-content {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 50px;
  align-items: end;
}

.featured-image-link {
  text-decoration: none;
  color: inherit;
  display: block;
}

.featured-image {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.featured-image:hover {
  transform: translateY(-5px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
}

.featured-image img {
  width: 100%;
  height: 400px;
  object-fit: cover;
}

.featured-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 50px 40px 40px;
  color: white;
}

.featured-overlay h2 {
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1.3;
  margin: 0;
}

.featured-details {
  display: flex;
  flex-direction: column;
}

.post-meta {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.author-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.written-by {
  font-size: 14px;
  color: #64748b;
}

.author-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(45deg, #1297ef, #4ab7ff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.author-name {
  font-weight: 500;
  color: #1e293b;
}

.publish-date {
  font-size: 14px;
  color: #64748b;
}

.post-categories {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.file-under {
  font-size: 14px;
  color: #64748b;
}

.category-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.category-tag {
  padding: 6px 12px;
  background: #f1f5f9;
  color: #475569;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.read-time {
  padding: 6px 12px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

/* Blog Posts Grid */
.blog-posts {
  padding: 100px 0;
  background: #f8fafc;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 60px;
}

.section-header h2 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.view-all-btn {
  padding: 12px 24px;
  background: linear-gradient(45deg, #1297ef, #4ab7ff);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.view-all-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(18, 151, 239, 0.3);
}

.posts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
}

.post-card-link {
  text-decoration: none;
  color: inherit;
  display: block;
}

.post-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.post-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
}

.post-image {
  height: 200px;
  overflow: hidden;
}

.post-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.post-card:hover .post-image img {
  transform: scale(1.05);
}

.post-content {
  padding: 30px;
}

.post-meta {
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 20px;
}

.post-category {
  font-size: 12px;
  color: #1297ef;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.post-date {
  font-size: 12px;
  color: #64748b;
}

.post-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 15px;
  line-height: 1.4;
}

.post-excerpt {
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 25px;
}

.post-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
}

.post-author {
  color: #475569;
  font-weight: 500;
}

.post-read-time {
  color: #64748b;
}

/* Newsletter Section */
.newsletter {
  padding: 80px 0;
  background: linear-gradient(135deg, #1297ef 0%, #4ab7ff 100%);
  color: white;
}

.newsletter-content {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
}

.newsletter-content h2 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 20px;
}
.parrafo {
  margin: 0px 0px 40px 0px;
}

.newsletter-content p {
  font-size: 1.125rem;
  margin-bottom: 40px;
  opacity: 0.9;
}

.newsletter-form {
  display: flex;
  gap: 12px;
  max-width: 400px;
  margin: 0 auto;
}

.newsletter-input {
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
}

.newsletter-btn {
  padding: 12px 24px;
  background: white;
  color: #1297ef;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.newsletter-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(255, 255, 255, 0.3);
}

/* ===== SKELETON LOADER STYLES ===== */

.skeleton-container {
  padding: 40px 0;
}

/* Featured Post Skeleton */
.featured-post-skeleton {
  margin-bottom: 60px;
}

.featured-skeleton-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;
}

.featured-skeleton-image {
  width: 100%;
  height: 400px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
}

.featured-skeleton-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.skeleton-author {
  display: flex;
  align-items: center;
  gap: 12px;
}

.skeleton-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-author-text {
  width: 120px;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-date {
  width: 180px;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-title {
  width: 100%;
  height: 32px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-tags {
  display: flex;
  gap: 8px;
}

.skeleton-tag {
  width: 80px;
  height: 24px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
}

/* Posts Grid Skeleton */
.posts-skeleton {
  margin-top: 60px;
}

.section-header-skeleton {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
}

.skeleton-section-title {
  width: 200px;
  height: 32px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-button {
  width: 150px;
  height: 40px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

.posts-grid-skeleton {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
}

.post-card-skeleton {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.skeleton-post-image {
  width: 100%;
  height: 200px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-post-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-post-category {
  width: 80px;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 10px;
}

.skeleton-post-title {
  width: 100%;
  height: 24px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-post-excerpt {
  width: 100%;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-post-excerpt:nth-child(2) {
  width: 80%;
}

.skeleton-post-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.skeleton-post-author {
  width: 100px;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-post-date {
  width: 80px;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

/* Shimmer Animation */
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-content h1 {
    font-size: 2.5rem;
  }

  .featured-content {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .featured-overlay h2 {
    font-size: 1.5rem;
  }

  .section-header {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }

  .section-header h2 {
    font-size: 2rem;
  }

  .posts-grid {
    grid-template-columns: 1fr;
  }

  .newsletter-form {
    flex-direction: column;
  }

  .newsletter-content h2 {
    font-size: 2rem;
  }

  /* Skeleton Responsive */
  .featured-skeleton-content {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .featured-skeleton-image {
    height: 300px;
  }

  .posts-grid-skeleton {
    grid-template-columns: 1fr;
  }

  .section-header-skeleton {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 15px;
  }

  .blog-hero {
    padding: 60px 0 40px;
  }

  .hero-content h1 {
    font-size: 2rem;
  }

  .featured-post {
    padding: 40px 0;
  }

  .blog-posts {
    padding: 60px 0;
  }
}
</style>
